import { create } from 'zustand';
import type { UsersStore } from '../interfaces/storeInterfaces/usersStore';
import { whitelistService } from '../services/whitelist.service';
import type { WhitelistUserParams, CreateWhitelistUserRequest, UpdateWhitelistUserRequest } from '../interfaces/apiInterfaces/whitelist';

export const useUsersStore = create<UsersStore>((set) => ({
  // State
  users: [],
  selectedUser: null,
  pagination: null,
  isLoading: false,
  error: null,

  // Actions
  loadUsers: async (params?: WhitelistUserParams) => {
    set({ isLoading: true, error: null });
    try {
      const response = await whitelistService.getAllWhitelistUsers(params);
      set({
        users: response.data,
        pagination: response.pagination,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load users',
        isLoading: false,
      });
    }
  },

  addUser: async (userData: CreateWhitelistUserRequest) => {
    set({ isLoading: true, error: null });
    try {
      await whitelistService.createWhitelistUser(userData);
      // Reload current page after successful addition
      const { pagination, loadUsers } = useUsersStore.getState();
      await loadUsers({
        page_number: 1, // Reset to first page to see the new user
        page_size: pagination?.page_size || 100,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to add user';
      set({
        error: errorMessage,
        isLoading: false,
      });
      throw error;
    }
  },

  updateUser: async (userId: string | number, userData: UpdateWhitelistUserRequest) => {
    set({ isLoading: true, error: null });
    try {
      await whitelistService.updateWhitelistUser(userId, userData);
      // Reload current page after successful update
      const { pagination, loadUsers } = useUsersStore.getState();
      await loadUsers({
        page_number: pagination?.page_number || 1,
        page_size: pagination?.page_size || 100,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update user';
      set({
        error: errorMessage,
        isLoading: false,
      });
      throw error;
    }
  },

  deleteUser: async (userId: string | number) => {
    set({ isLoading: true, error: null });
    try {
      await whitelistService.deleteWhitelistUser(userId);
      // Reload current page after successful deletion
      const { pagination, loadUsers } = useUsersStore.getState();
      await loadUsers({
        page_number: pagination?.page_number || 1,
        page_size: pagination?.page_size || 100,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to delete user',
        isLoading: false,
      });
      throw error;
    }
  },

  clearSelectedUser: () => {
    set({ selectedUser: null });
  },

  clearError: () => {
    set({ error: null });
  },
}));
